/// <amd-module name="Build/ArtifactsView/Components/TreeColumnAction" />
import * as React from "react";
import * as Resources from "Build/ArtifactsView/Resources";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { IVssPageContext } from "VSS/Platform/Context";
import { ClipboardButton, copyToClipboard } from "VSSUI/Clipboard";
import { Observer } from "VSSUI/Observer";
import { TableColumnLayout } from "VSSUI/Table"
import { ITreeColumn } from "VSSUI/TreeEx";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { css, preventDefault } from "VSSUI/Util";
import { Location } from "VSSUI/Utilities/Position";
import { ObservableValue, ObservableArray } from "VSS/Core/Observable";
import { MoreButton, IMenuItem } from "VSSUI/Menu";

export interface TreeColumnActionProps<T> {
    pageContext: IVssPageContext;
    copyAvailable: (treeItem: T) => boolean;
    getCopyUrl: (treeItem: T) => string;
    downloadAvailable: (treeItem: T) => boolean;
    downloadItem: (treeItem: T) => void;
    getDownloadUrl: (treeItem: T) => Promise<string | undefined>;
}
export class TreeColumnAction<T> implements ITreeColumn<T> {
    public columnLayout = TableColumnLayout.none;
    public id = "_action";
    public width = 42;

    private pageContext: IVssPageContext;

    private downloadAvailable: (treeItem: T) => boolean;
    private downloadItem: (treeItem: T) => void;
    private getDownloadUrl: (treeItem: T) => Promise<string | undefined>;

    private copyAvailable: (treeItem: T) => boolean;
    private getCopyUrl: (treeItem: T) => string;
    private downloadUrl: ObservableValue<string | undefined> = new ObservableValue(undefined);

    constructor(props: TreeColumnActionProps<T>) {
        this.pageContext = props.pageContext;
        this.downloadAvailable = props.downloadAvailable;
        this.downloadItem = props.downloadItem;
        this.copyAvailable = props.copyAvailable;
        this.getCopyUrl = props.getCopyUrl;
        this.getDownloadUrl = props.getDownloadUrl;
    }

    public renderCell = (rowIndex: number, columnIndex: number, tableColumn: ITreeColumn<T>, treeItem: ITreeItemEx<T>): JSX.Element => {
        return (
            <td
                key="col-action"
                aria-colindex={columnIndex}
                className={css("bolt-table-cell-side-action bolt-table-cell bolt-list-cell", "col-" + columnIndex)}
                data-column-index={columnIndex}
                onClick={preventDefault}
                onDoubleClick={preventDefault}
                onMouseDown={preventDefault}
            >
                {this.renderActionButton(treeItem.underlyingItem.data)}
            </td>
        );
    };

    private renderActionButton = (treeItemData: T): JSX.Element | undefined => {
        let menuItems: ObservableArray<IMenuItem> = new ObservableArray([
            { 
                id: "download", 
                text: Resources.DownloadArtifacts, 
                className: "text-ellipsis",
                onActivate: () => this.downloadItem(treeItemData),
                href: this.downloadUrl.value,
                iconProps: { iconName: "Download" }
            }
        ]);
        this.getDownloadUrl(treeItemData).then(url => {
            this.downloadUrl.value = url;
            if (!!this.downloadUrl.value) {
                menuItems.push({
                    id: "copy", 
                    text: Resources.CopyArtifactUrl,
                    className: "text-ellipsis",
                    onActivate: (item: IMenuItem) => {
                        copyToClipboard(item.href as string);
                        this.showCopiedToast();
                    },
                    href: this.downloadUrl.value,
                    iconProps: { iconName: "Copy" }
                });
            }
        });
        if (this.downloadAvailable(treeItemData)) {
                        return <Observer href={this.downloadUrl} >
                           {() => (
                               <MoreButton
                                    className="bolt-table-cell-content-reveal justify-center"
                                    role="menuitem"
                                    contextualMenuProps={{
                                        menuProps: { id: "header-submenu", items: menuItems }
                                    }}
                                   subtle={true}
                               />
                           )}
                       </Observer>
        } else if (this.copyAvailable(treeItemData)) {
            return <ClipboardButton
                className="bolt-table-cell-content-reveal justify-center"
                getContent={() => { return this.getCopyUrl(treeItemData); }}
                onCopy={this.showCopiedToast}
                subtle
                tooltipProps={{ 
                    text: Resources.CopyFileShareLocation,
                    anchorOrigin: {
                        horizontal: Location.center,
                        vertical: Location.end
                    },
                    tooltipOrigin: {
                        horizontal: Location.center,
                        vertical: Location.start
                    }
                 }}
            />
        }

        return undefined;
    }

    public renderHeaderCell = (columnIndex: number, tableColumn: ITreeColumn<T>): JSX.Element => {
        return (
            <th
                key="col-action"
                className={css(tableColumn.headerClassName, "bolt-table-header-cell bolt-table-header-cell-empty", "col-header-" + columnIndex)}
                data-column-index={columnIndex}
            />
        );
    };

    public showCopiedToast = () => {
        const messageService = this.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        messageService.addToast({
            duration: 2000,
            message: Resources.CopiedToClipboard
        });
    }
}
